import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { fetchContent, subscribeToType, LIVE_MODE } from '../lib/sanity';
import Iridescence from '../components/Iridescence';
import CurvedLoop from '../components/CurvedLoop';
import PillNav from '../components/PillNav';
import MobileBanner from '../components/MobileBanner';

interface NavigationLink {
  text: string;
  url: string;
  isExternal: boolean;
}

interface HeroContent {
  title: string;
  subtitle: string;
  navigationLinks: NavigationLink[];
}

const Hero = () => {
  const [content, setContent] = useState<HeroContent | null>(null);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    const getHeroContent = async () => {
      try {
        const [heroData] = await fetchContent('hero');
        setContent(heroData);
        if (LIVE_MODE) {
          unsubscribe = subscribeToType('hero', (docs) => setContent(docs[0]));
        }
      } catch (error) {
        console.error('Error fetching hero content:', error);
      }
    };

    getHeroContent();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // PillNav configuration for Hero page
  const pillNavItems = [
    { label: 'Home', href: '/', ariaLabel: 'Go to home page' },
    { label: 'Longevity', href: '/main', ariaLabel: 'Go to longevity page' },
    { label: 'Travel', href: 'https://andreavillalobos.inteletravel.com/booktravel.cfm', ariaLabel: 'Book your trip' }
  ];

  // Placeholder logo - replace with your actual logo
  const logoUrl = '/favicon.ico';

  return (
    <section className="relative h-screen bg-linear-to-r from-blue-50 to-green-50">
      <Iridescence
        className="absolute inset-0 z-0"
        color={[0.4, 0.9, 0.9]}
        mouseReact={false}
        amplitude={0.1}
        speed={1.0}
      />

      {/* PillNav Component - Fixed at top right */}
      <PillNav
        logo={logoUrl}
        logoAlt="Logo"
        items={pillNavItems}
        activeHref="/"
        baseColor="#ccfcf1"
        pillColor="#006D6D"
        pillTextColor="#ffffff"
        hoveredPillTextColor="#006D6D"
        className="md:right-6"
        initialLoadAnimation={true}
      />

      {/* Mobile Banner - Prompts users to tap hamburger menu */}
      <MobileBanner />

      <div className="container mx-auto px-6 relative z-10 h-full flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center w-full"
        >
          <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6">
            {content?.title || 'Andrea Villalobos'}
          </h1>
          <p className="text-xl md:text-3xl text-gray-800 mb-4 max-w-3xl mx-auto">
            {content?.subtitle || 'Non-medical remote GLP-1 accountability & longevity support alongside licensed clinical care'}
          </p>
          <p className="text-base md:text-lg text-gray-700 mb-12 max-w-2xl mx-auto">
            Andrea is not a licensed healthcare provider. This is lifestyle accountability support, not a substitute for clinical care.
          </p>
          <div className="relative z-20 w-full">
            <CurvedLoop 
              marqueeText="Build Steady Routines ✦ Explore The World ✦ Live Fully ✦"
              speed={2}
              curveAmount={400}
              direction="left"
              interactive={true}
              height="h-64"
              className="drop-shadow-lg"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
